import api from './api';

interface UploadUrlResponse {
  uploadUrl: string;
  key: string;
  expiresIn: number;
}

interface DownloadUrlResponse {
  downloadUrl: string;
  key: string;
  expiresIn: number;
}

export const awsService = {
  /**
   * Generate signed URL for uploading to S3
   */
  async generateUploadUrl(
    key: string,
    contentType: string,
    expiresIn: number = 300
  ): Promise<UploadUrlResponse> {
    const response = await api.post('/aws/generate-upload-url', {
      key,
      contentType,
      expiresIn,
    });
    return response.data;
  },

  /**
   * Upload file directly to S3 using signed URL
   * Uses native fetch API to avoid webpack polyfill issues
   */
  async uploadToS3(
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded * 100) / e.total);
            onProgress(progress);
          }
        });
      }

      // Handle completion
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      // Handle errors
      xhr.addEventListener('error', () => {
        reject(new Error('Upload failed due to network error'));
      });

      // Handle abort
      xhr.addEventListener('abort', () => {
        reject(new Error('Upload was aborted'));
      });

      // Start upload
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  },

  /**
   * Complete upload flow: get signed URL + upload to S3
   */
  async uploadFile(
    file: File,
    keyPrefix: string,
    onProgress?: (progress: number) => void
  ): Promise<string> {
    const extension = file.name.split('.').pop();
    const timestamp = Date.now();
    const key = `${keyPrefix}/${timestamp}.${extension}`;

    // Get signed URL
    const { uploadUrl } = await this.generateUploadUrl(key, file.type);

    // Upload to S3
    await this.uploadToS3(uploadUrl, file, onProgress);

    return key;
  },

  /**
   * Generate signed URL for downloading from S3
   */
  async generateDownloadUrl(
    key: string,
    expiresIn: number = 3600
  ): Promise<DownloadUrlResponse> {
    const response = await api.post('/aws/generate-download-url', {
      key,
      expiresIn,
    });
    return response.data;
  },
};

